import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { PillIcon, ShieldCheckIcon, ClockIcon, BellIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
export function MedicationRemindersPage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-888">
      <PageBanner title="Medication Reminders" subtitle="Ensuring safety and compliance through timely medication support." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Medication Reminders',
      path: '/services/medication-reminders'
    }]} data-id="element-889" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-light-alt shadow-inner" data-id="element-890">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-891">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-892">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-893">
              <PillIcon className="h-4 w-4 text-primary" data-id="element-894" />
              <span className="text-primary text-sm font-semibold" data-id="element-895">
                Safety & Compliance
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-896">
              Peace of Mind Through Reliable Medication Support
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-897">
              <p data-id="element-898">
                Managing multiple medications can be complex and overwhelming.
                Our medication reminder services are designed to ensure that you
                or your loved one never misses a dose, helping to maintain
                health and prevent complications associated with medication
                errors.
              </p>
              <p data-id="element-899">
                Our caregivers provide gentle, timely reminders and assist with
                opening containers, ensuring that the right medication is taken
                at the right time, exactly as prescribed by your physician.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-900">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-901" />
              <div data-id="element-902">
                <h4 className="font-bold text-dark" data-id="element-903">RN Supervised</h4>
                <p className="text-sm text-dark-muted" data-id="element-904">
                  All medication schedules are reviewed and monitored by
                  Registered Nurses for your safety.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-light shadow-inner" data-id="element-905">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-906">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-907">
            <div className="text-center mb-14" data-id="element-908">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-909">
                Why Medication Reminders Matter
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-910"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-911">
              {[{
              icon: BellIcon,
              title: 'Timely Dosing',
              desc: 'Ensuring medications are taken at the precise intervals prescribed.'
            }, {
              icon: ShieldCheckIcon,
              title: 'Error Prevention',
              desc: 'Reducing the risk of missed doses or accidental double-dosing.'
            }, {
              icon: ClockIcon,
              title: 'Consistency',
              desc: 'Maintaining a stable therapeutic level of medication in the body.'
            }, {
              icon: AlertCircleIcon,
              title: 'Symptom Monitoring',
              desc: 'Caregivers can observe and report potential side effects to the RN.'
            }].map((benefit, idx) => <div key={idx} className="bg-light-alt rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-912">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-913">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-914" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-915">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-916">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-light-alt shadow-inner" data-id="element-917">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-918">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-919">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-920">
              Our Medication Support Includes
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-921">
              We provide a supportive environment to ensure medication
              compliance while respecting the patient's independence.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-922">
              {['Verbal reminders to take medications', 'Assistance with opening pill bottles', 'Reading medication labels', 'Monitoring for potential side effects', 'RN review of medication lists', 'Coordination with pharmacy and physicians', 'Documentation of medication compliance', 'Education for patients and families'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light rounded-xl border border-gray-200" data-id="element-923">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-924" />
                  <span className="text-dark font-medium" data-id="element-925">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light" data-id="element-926">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-927">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-928">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-929"></div>
            <div className="relative z-10" data-id="element-930">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-931">
                Need Help with Medication?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-932">
                Contact us today to discuss how our medication reminder services
                can provide peace of mind and ensure safety for you or your
                loved one.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-933">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-934" />
    </main>;
}